import { FormInput, FormQuerySelector, Toast } from "@vesatogo/grass-core"
import classNames from "classnames"
import { isPlainObject, omit } from "lodash-es"
import { InferType } from "yup"
import { Mohadi } from "~/components/Map/location.data"
import { gisToJson } from "~/components/Map/map-helpers"
import MapAddress from "~/components/Map/MapAddress"
import { baseAddressModel } from "~/models/address"
export type AddressState = InferType<typeof baseAddressModel>
export type AddressFormProps = {
  getInputProps?: any
  setValues?: any
  children?: React.ReactNode
  mapWidth?: string
  mapClassName?: string
  formClassName?: string
  disabled?: boolean
  className?: string
  onPolyComplete?: any
  address?: any
  addressKey?: string
  compact?: boolean
  noteClassName?: string
  showMap?: boolean
  showRawAddress?: boolean
  conciseView?: boolean // New prop
  mapChildren?: React.ReactNode
}

const AddressForm = ({
  className,
  children,
  formClassName,
  mapClassName,
  disabled,
  onPolyComplete,
  getInputProps,
  setValues,
  mapWidth = "40rem",
  address,
  addressKey = "address",
  compact = false,
  noteClassName,
  showMap,
  showRawAddress = false,
  conciseView = false,
  mapChildren,
}: AddressFormProps) => {
  if (!isPlainObject(address)) return null
  return (
    <div className={classNames("flex gap-6", className)}>
      {compact || showMap === false ? null : (
        <MapAddress
          onPolyComplete={onPolyComplete}
          customMarker={true}
          className={classNames(
            "w-max shadow-sm overflow-hidden rounded-lg",
            mapClassName
          )}
          mapClassName="!mr-0 !rounded-none"
          showFunctionalities
          value={address?.location ? gisToJson(address?.location) : Mohadi}
          handleLocationData={data => {
            if (disabled) return

            setValues({
              [addressKey]: {
                id: address?.id,
                ...omit(data, showRawAddress ? "raw_address" : ""),
              },
            })
          }}
          mapStyle={{
            height: conciseView ? "130px" : "25rem", // Adjust height based on conciseView
            width: mapWidth,
          }}
          footer={
            !conciseView && address ? (
              <div className="p-2 rounded-lg gap-3">
                <p>
                  <span className="font-500 text-gray-800">
                    {address?.local_area || "Area"} (
                    {address?.district || "district"})
                  </span>{" "}
                  <span className="text-gray-600">
                    {address?.location?.coordinates[1]?.toFixed(6)},{" "}
                    {address?.location?.coordinates[0]?.toFixed(6)}
                  </span>
                </p>
                <p className="max-w-[38rem]">{address.raw_address}</p>
              </div>
            ) : null
          }
        >
          {mapChildren}
        </MapAddress>
      )}
      <div
        className={classNames(
          "grid gap-3 h-max",
          compact ? "grid-cols-1" : "grid-cols-2",
          formClassName
        )}
      >
        {children}
        {conciseView ? null : (
          <>
            <FormInput
              data-testid="inputArea"
              label="Area"
              required
              className={compact ? "col-span-2" : null}
              {...getInputProps(`${addressKey}.local_area`)}
              disabled={disabled}
            />
            <FormInput
              data-testid="inputVillage"
              label="Village"
              required
              {...getInputProps(`${addressKey}.village`)}
              disabled={disabled}
            />
            <FormInput
              data-testid="inputTaluka"
              label="Taluka"
              required
              {...getInputProps(`${addressKey}.taluka`)}
              disabled={disabled}
            />
            <FormInput
              data-testid="inputDistrict"
              label="District"
              required
              {...getInputProps(`${addressKey}.district`)}
              disabled={disabled}
            />
            <FormInput
              data-testid="inputState"
              label="State"
              {...getInputProps(`${addressKey}.state`)}
              disabled
            />
            <FormInput
              data-testid="inputCountry"
              label="Country"
              {...getInputProps(`${addressKey}.country`)}
              disabled
            />
            <FormInput
              data-testid="inputPincode"
              label="Pincode"
              required
              {...getInputProps(`${addressKey}.pincode`)}
              disabled={disabled}
            />
            {/* <FormQuerySelector
              data-testid="inputSubregion"
              label="Sub Region"
              dataHook={useRegionSelectorQuery}
              variables={{
                where: {
                  region_group: { codename: { _eq: Codenames.SUB_REGION } }
                }
              }}
              {...getInputProps(`${addressKey}.sub_region`)}
              disabled={disabled}
            /> */}
            <div
              className={classNames(
                compact ? "col-span-2 mb-3" : "col-span-2",
                noteClassName
              )}
            >
              <Toast
                intent="info"
                title={
                  <span>
                    Please select the location by dragging the pin or searching
                    via the search bar, the actual location{" "}
                    <b>will not update</b> by editing the fields
                  </span>
                }
                variant="minimal"
                className="font-400 max-w-full"
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AddressForm
